import { Box, Divider, TextField, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import CartContentItem from './CartContentItem';
import { checkIfMeasKitSizeExists, calculateCompleteCartInvoice, getEndShipmentPrice, getNumberOfItems, getShipmentPriceSubsidyAmount, submitCouponRequest } from '../redux/CartRedux';
import { CartStateItem, CartState } from 'common-ts/dist/models/Cart';
import { useAppDispatch } from '../reduxhooks';
import { ProductsList } from 'common-ts/dist/models/Product';
import { useFormik } from 'formik';
import * as yup from 'yup';
import OverlayComponentForm from './OverlayComponentForm';
import OverlayComponentButton from './OverlayComponentButton';
import OverlayComponentBaseContainerHorizontal from './OverlayComponentBaseContainerHorizontal';
import { Mixpanel } from '../mixpanel';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { OngkirBanner } from './OverlayViewPopupOngkir';
import { useEffect, useState } from 'react';
import OverlayViewLabelNamaUpdate from './OverlayViewLabelNamaUpdate';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cartContent: {
      display: 'block',
      position: 'relative',
      textAlign: 'center',
    },
    totalAmount: {
      padding: '5px 0px',
      display: 'flex',
      justifyContent: 'space-between',
    },
    dividerItemsTotal: {
      marginTop: '40px',
      marginBottom: '12px',
    },
    dividerCartTotal: {
      marginTop: '12px',
      marginBottom: '12px',
    },
    priceText: {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '15px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    priceStrikeText: {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '14px',
      textDecoration: 'line-through',
      marginRight: '10px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    shipmentText: {
      fontSize: '15px',
    },
    discountLabelText: {
      color: 'green',
    },
    discountKeyText: {
      fontSize: '15px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    discountPriceText: {
      color: 'green',
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '15px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '13px',
      }
    },
    potonganOngkir: {
      padding: '5px 0px 0px 0px',
      display: 'flex',
      justifyContent: 'space-between',
      color: "#09B038"
    },
    potonganOngkirSubtextBox: {
      padding: '2px 0px 5px 0px',
      display: 'flex',
      justifyContent: 'space-between',
      color: "#09B038"
    },
    potonganOngkirTextIcon: {
      padding: "0 5px"
    },
    potonganOngkirText: {
      fontSize: "13px",
      fontWeight: 600,
      letterSpacing: "0.7px",
    },
    potonganOngkirSubtext: {
      fontSize: "10px",
      fontWeight: 400,
      letterSpacing: "0.4px",
    }
  }),
);

const validationSchema = yup.object({
  contentKey: yup.string()
  .min(5, '')
  .required('Masukkan kode promo kamu disini'),
});

export function CartCouponInput() {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const isMdOrUp = useMediaQuery(theme.breakpoints.up("md"));

  const formik = useFormik({
    initialValues: {
      contentKey: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      Mixpanel.track('couponKeyInput', {
        'couponKey': values.contentKey,
      })
      dispatch(submitCouponRequest(values.contentKey));
    },
  });

  let children: JSX.Element[] = [];
  
  children.push(
    <TextField
      margin="dense"
      fullWidth
      id="contentKey"
      name="contentKey"
      label="Kode promo"
      type="text"
      value={formik.values.contentKey}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={false}
      helperText={(formik.touched.contentKey && formik.errors.contentKey)}
      InputLabelProps={{
        shrink: true,
      }}
    />
  );

  let submitChildren: JSX.Element[] = [];

  submitChildren.push(
    <OverlayComponentButton
      isTypeSubmit={true}
      formButtonText="Gunakan Kode"
      buttonMarginTopBottom="10px"
      buttonMarginLeftRight="0px"
    />
  )

  return (
    <OverlayComponentForm
      formTitle=""
      formSubmitBody={submitChildren}
      formFormBody={children}
      formHandleSubmit={formik.handleSubmit}
      titleWidthMdUp='400px'
      titleWidthSmDown='300px'
      formWidthMdUp='420px'
      formWidthSmDown='100%'
      formJustifyContent='space-evenly'
      formAlignItems='flex-start'
    />
  )
}

function getLabelStartIndexes(cartProductList: any) {
  const returnValue: number[] = [];
  let sum = 0;

  for (let i = 0; i < cartProductList.length; i++) {
    returnValue.push(sum);
    sum += cartProductList[i].amount;
  }

  return returnValue;
}

// VERSION JULY 20, 2024
function getConcatNamePreserveFrontBack(inputName: string) {
  const firstLetter = inputName[0];
  const lastLetter = inputName[inputName.length - 1];

  const wordWithoutFirstLast = inputName.substring(1, inputName.length - 1);

  let wordWithoutFirstLastConcat = wordWithoutFirstLast.replace(/[aeiouAEIOU]/g, '');
  let completeWordWithoutFirstLastConcat = firstLetter + wordWithoutFirstLastConcat + lastLetter;

  return completeWordWithoutFirstLastConcat;
}

// VERSION JULY 20, 2024
function getLabelName(currentName: string) {
  // ORDER OF PRIORITIES - July 20, 2024
  // 1. First-met name, more than 2 characters long, unedited
  // 2. Initials, given there's 2 or more words
  // 3. Second-met name, third-met name, and so forth
  // 4. First-met name, more than 2 characters long, concatenated
  // 5. First-met name, more than 2 characters long, 3 first characters

  // NEXT IMPROVEMENTS
  // 1. Concat to max letters, try to remove as few vowels as possible

  const capsName = currentName.toUpperCase().replace(/[^a-zA-Z ]+/g, '');;
  const capsNameList = capsName.split(" ");

  let labelName = "";

  let initials = "";
  let validConcatName = "";
  let validConcatNameSuper = "";

  let startingIndex = -1;

  const maxLetters = 7;

  for (let capNameIndex = 0; capNameIndex < capsNameList.length; capNameIndex = capNameIndex + 1) {

    if (capsNameList?.[capNameIndex].length > 1) {
      initials = initials + capsNameList?.[capNameIndex][0];
    }

    if (capsNameList?.[capNameIndex].length > 2) {

      if (startingIndex === -1) {
        startingIndex = capNameIndex;
      }
      let labelNameWithoutI = capsNameList?.[capNameIndex].replace(/I/g, '');
      let concatName = getConcatNamePreserveFrontBack(capsNameList[capNameIndex]);
      let concatNameWithoutI = concatName.replace(/I/g, '');

      // Consider first name, max 6 huruf kecuali I
      if ((capsNameList[capNameIndex].length <= maxLetters || labelNameWithoutI?.length <= maxLetters) && labelName === "") {
        labelName = capsNameList[capNameIndex];
      }
      // Get concatenated version (only the first one)
      else if ((concatName?.length <= maxLetters || concatNameWithoutI?.length <= maxLetters) && validConcatName === "") {
        validConcatName = concatName;
      } else if (validConcatName === "") {
        validConcatNameSuper = capsNameList[capNameIndex].substring(0, 3);
      }
    }
  }

  if (startingIndex !== -1 && labelName !== capsNameList[startingIndex] && initials.length > 1) {
    labelName = initials.substring(0, maxLetters);
  }
  if (labelName === "" && validConcatName.length > 1) {
    labelName = validConcatName;
  }
  if (labelName === "" && validConcatNameSuper.length > 1) {
    labelName = validConcatNameSuper;
  }

  if (capsName === "MARKETPLACE") {
    labelName = "SDS"
  }

  return labelName;
}

interface CartContentProps {
  orderObject: any,
  productsList: ProductsList,
  isModifiable: boolean
  withPromoField?: boolean
  showWarning?: boolean
  withUpdateLabel?: boolean
}

// productsList in this argument is derived from orderObject, NOT current productsList.
function CartForOrderContent({ orderObject, productsList, isModifiable, withPromoField, showWarning, withUpdateLabel }: CartContentProps) {
  const cartProductList = orderObject.cartProductList;
  const cartShipmentPrice = orderObject.shipmentPrice;
  const classes = useStyles();
  const theme = useTheme();

  const completeCartInvoiceObj = calculateCompleteCartInvoice(orderObject, productsList, {}, {});
  const originalPriceAmount = completeCartInvoiceObj.totalAmountObj.originalPriceAmount;
  const discountedPriceAmount = completeCartInvoiceObj.totalAmountObj.discountedPriceAmount;
  const discountedPriceAmountWithPromotions = orderObject.totalCartAmount;
  const discountedPriceAmountWithPromotionsAndShipment = orderObject.totalPaymentAmount;

  const cartItemsWithCouponPromotions = completeCartInvoiceObj.cartItemsWithCouponPromotions;

  const numberOfItemsInCart = getNumberOfItems(orderObject);
  const shipmentMaxPriceCut = getShipmentPriceSubsidyAmount(numberOfItemsInCart, discountedPriceAmountWithPromotions, orderObject?.couponKey ?? "");
  const endCartShipmentPrice = getEndShipmentPrice(discountedPriceAmountWithPromotions, getNumberOfItems(orderObject), orderObject.shipmentPrice, orderObject?.couponKey ?? "") as number;

  // Tambahan label nama
  const changeLabelDeadline = new Date(new Date(orderObject.paymentDateTime || orderObject.creationDateTime).getTime() + 30 * 60 * 1000).toISOString(); // 30 Minutes Time
  // const changeLabelDeadline = new Date(Date.now() + 30 * 1000).toISOString(); // for testing only

  const labelNamaManualArray = orderObject.labelNamaManualArray || new Array(numberOfItemsInCart).fill(getLabelName(orderObject?.orderName ?? "KAMU"));

  const startIndexes = getLabelStartIndexes(cartProductList);

  const [labelNamaDialogOpen, setLabelNamaDialogOpen] = useState(false);
  const [selectedLabelNama, setSelectedLabelNama] = useState<null | number>(null);
  const labelNamaDialogClose = () => {
    setLabelNamaDialogOpen(false);
    setSelectedLabelNama(null);
  }
  // Label nama sampai sini

  return (
    <div className={classes.cartContent}>
      <div>
        {
        showWarning ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '5px 0px 25px 0px' }}>
            <img
              style={{ 
                width: '330px', 
                height: 'auto', 
                objectFit: 'contain', 
                [theme.breakpoints.up('xs')]: {
                width: '400px',
              } }}
              src="https://studiosclo.uk/general/ORDER_FINAL_WARNING.png"
              alt="Order final warning" // Added alt text for accessibility
            />
          </div>
        ) : (
          <div />
        )}
        {
          cartProductList.map((item: CartStateItem, index: number) => {
            const labelProps = withUpdateLabel ? {
              startIndex: startIndexes?.[index],
              labelNamaManualArray,
              setLabelNamaDialogOpen: setLabelNamaDialogOpen,
              setSelectedLabelNama: setSelectedLabelNama,
              changeLabelNamaDeadline: changeLabelDeadline
            } : {};

            return (
              <CartContentItem showDetailEditButton={true} hideDiscountedPrice={true} cartItem={item} productsList={productsList} 
              cartItemsWithCouponPromotions={cartItemsWithCouponPromotions}
              isModifiable={isModifiable} parentRenderCallback={(renderTime) => {
                // TODO TODO TODO
                // Currently ignoring render time callback
              }}
              {...labelProps}
              />
            )
          })
        }
      </div>
      <Divider className={classes.dividerItemsTotal} />
      {
        withPromoField === false ? 
        null :
        <CartCouponInput/>
      }
      {
        withPromoField === false ? 
        null :
        <OngkirBanner/>
      }
      {
        withPromoField === false ? 
        null :
        <Divider className={classes.dividerCartTotal} />
      }
      {
        orderObject.couponKey ? 
        <div className={classes.totalAmount}>
          <Typography variant="body1">
            <b>
              PROMO CODE:
            </b> 
          </Typography>
          <Typography className={classes.discountKeyText}>
            {orderObject.couponKey?.startsWith("AJAKTEMEN") ? "AJAKTEMEN" : orderObject.couponKey}
          </Typography>
        </div> : 
        null
      }
      <div className={classes.totalAmount}>
        <Typography variant="body1">
          <b>
            ITEM(S) SUBTOTAL:
          </b> 
        </Typography>
        <Typography className={classes.priceText}>
          {/* <span className={classes.priceStrikeText}>
            { 
            originalPriceAmount !== discountedPriceAmount ?
            "Rp " + originalPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
            ""
            }
          </span> */}
          Rp { discountedPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }
        </Typography>
      </div>
      {
        orderObject.couponKey ? 
        <div className={classes.totalAmount}>
          <Typography variant="body1" className={classes.discountLabelText}>
            <b>
              PROMOTIONS:
            </b> 
          </Typography>
          <Typography className={classes.discountPriceText}>
            <b>-</b> Rp {(discountedPriceAmount - discountedPriceAmountWithPromotions).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </Typography>
        </div> : 
        null
      }
      <div className={classes.totalAmount}>
        <Typography variant="body1">
          <b>
            SHIPPING:
          </b> 
        </Typography>
        { 
        cartShipmentPrice === undefined ?
        <Typography className={classes.shipmentText}>
          -
        </Typography> :
        <Typography className={classes.priceText}>
          {"Rp " + cartShipmentPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </Typography>
        } 
      </div>
      {
        numberOfItemsInCart > 0 ?
        <div className={classes.potonganOngkir}>
          <Typography variant="body1" className={classes.potonganOngkirText}>
              POTONGAN ONGKIR<span className={classes.potonganOngkirTextIcon}>&#x24D8;</span>:
          </Typography>
          {
            cartShipmentPrice === undefined ? 
            <Typography className={classes.priceText}>
              {"Up to Rp " + shipmentMaxPriceCut.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>:
            <Typography className={classes.priceText}>
              {"- Rp " + (cartShipmentPrice - endCartShipmentPrice).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
          }
        </div>
        : null
      }
      {
        numberOfItemsInCart > 0 ?
        <div className={classes.potonganOngkirSubtextBox}>
          <Typography variant="body1" className={classes.potonganOngkirSubtext}>
            Selamat! Kamu mendapat potongan ongkir!
          </Typography>
        </div>
        : null
      }
      <div className={classes.totalAmount}>
        <Typography variant="body1">
          <b>
            TOTAL:
          </b> 
        </Typography>
        <Typography className={classes.priceText}>
          <span className={classes.priceStrikeText}>
            { 
            discountedPriceAmount === discountedPriceAmountWithPromotions ?
            "" :
            cartShipmentPrice === undefined ?
            "Rp " + discountedPriceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
            "Rp " + (discountedPriceAmount + cartShipmentPrice).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
          </span>
          Rp { 
          cartShipmentPrice === undefined ?
          discountedPriceAmountWithPromotions.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
          discountedPriceAmountWithPromotionsAndShipment.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
        </Typography>
      </div>
      <OverlayViewLabelNamaUpdate onClose={labelNamaDialogClose} open={labelNamaDialogOpen} userSub={orderObject.userSub} creationDateTime={orderObject.creationDateTime} changeLabelDeadline={changeLabelDeadline} labelNamaManualArray={labelNamaManualArray} labelNamaIndex={selectedLabelNama}/>
    </div>
  );
}

export default CartForOrderContent;